<template>
  <div class="page rates-page">
    <section class="rates-hero">
      <div class="hero-content">
        <p class="eyebrow">{{ isEnglish ? 'Nyenyezia Forex' : 'Nyenyezia Forex' }}</p>
        <h1>{{ isEnglish ? 'Exchange Rates' : 'Taux de change' }}</h1>
        <p>
          {{ isEnglish
            ? 'Indicative public rates for clients. Rates may vary before a transaction is confirmed.'
            : 'Taux publics indicatifs pour les clients. Les taux peuvent varier avant la confirmation d’une opération.' }}
        </p>
      </div>
    </section>

    <main class="rates-content">
      <div class="rates-intro">
        <div>
          <p class="eyebrow">{{ isEnglish ? 'Public board' : 'Tableau public' }}</p>
          <h2>{{ isEnglish ? 'Current client rates' : 'Taux clients actuels' }}</h2>
        </div>
        <div class="update-pill">
          <span>{{ isEnglish ? 'Last updated' : 'Dernière mise à jour' }}</span>
          <strong>{{ lastUpdatedLabel || (isEnglish ? 'Pending' : 'À confirmer') }}</strong>
        </div>
      </div>

      <div v-if="loading" class="status-card">
        <span class="status-dot"></span>
        {{ isEnglish ? 'Loading rates...' : 'Chargement des taux...' }}
      </div>
      <div v-if="errorMessage" class="status-card error">
        {{ errorMessage }}
      </div>

      <section class="currency-module">
        <div class="rates-card-header">
          <div>
            <p class="eyebrow">{{ isEnglish ? 'Public rates' : 'Taux publics' }}</p>
            <h3>{{ isEnglish ? 'Exchange rates in CAD' : 'Taux en dollars canadiens' }}</h3>
            <p class="section-lead">Taux en dollars canadiens (CAD)</p>
          </div>
        </div>

        <div class="table-wrap">
          <table class="currency-table">
            <thead>
              <tr>
                <th scope="col" rowspan="2">Devise</th>
                <th scope="col">Achat</th>
                <th scope="col">Vente</th>
              </tr>
              <tr class="currency-table-subhead">
                <th scope="col">CAD</th>
                <th scope="col">CAD</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="rate in displayRates" :key="rate.currency_code">
                <td>
                  <div class="currency-cell" :title="displayCurrencyName(rate)">
                    <CountryFlag :iso="countryCodePerCurrency[rate.currency_code] || rate.currency_code.slice(0, 2)" mode="squared" />
                    <p class="currency-table-text">{{ rate.currency_code }} {{ displayCurrencyName(rate) }}</p>
                  </div>
                </td>
                <td class="digital-text">{{ formatRate(rate.buy_rate) }}</td>
                <td class="digital-text">{{ formatRate(rate.sell_rate) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="rate-notes">
          <p>{{ isEnglish ? 'Rates are provided for information and may change without notice.' : 'Les taux sont fournis à titre indicatif et peuvent changer sans préavis.' }}</p>
          <p>{{ isEnglish ? 'Specific cash denomination requests may be subject to additional fees.' : 'Des demandes spécifiques de coupures peuvent entraîner des frais supplémentaires.' }}</p>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import CountryFlag from '@dzangolab/vue-country-flag-icon';
import '@dzangolab/vue-country-flag-icon/dist/CountryFlag.css';
import { fetchPublicRates } from '@/services/ratesService';

export default {
  components: { CountryFlag },
  data() {
    return {
      loading: false,
      errorMessage: '',
      rates: [],
      updatedAt: null,
      countryCodePerCurrency: {
        CAD: 'CA',
        USD: 'US',
        EUR: 'EU',
        GBP: 'GB',
        XOF: 'SN',
        XAF: 'CM',
        CDF: 'CD',
        CHF: 'CH',
        GNF: 'GN',
        NGN: 'NG',
        MAD: 'MA',
        AED: 'AE'
      }
    };
  },
  computed: {
    sortedRates() {
      const priority = ['USD', 'EUR', 'GBP', 'CHF', 'CAD'];
      return [...this.rates].sort((a, b) => {
        const priorityA = priority.indexOf(a.currency_code);
        const priorityB = priority.indexOf(b.currency_code);
        if (priorityA !== -1 || priorityB !== -1) {
          if (priorityA === -1) return 1;
          if (priorityB === -1) return -1;
          return priorityA - priorityB;
        }
        if (a.display_order === b.display_order) {
          return a.currency_code.localeCompare(b.currency_code);
        }
        return a.display_order - b.display_order;
      });
    },
    isEnglish() {
      return this.$route.path.startsWith('/en');
    },
    displayRates() {
      return this.sortedRates;
    },
    lastUpdatedLabel() {
      if (!this.updatedAt) return '';
      const date = new Date(this.updatedAt);
      if (Number.isNaN(date.getTime())) return '';
      return date.toLocaleString(this.isEnglish ? 'en-CA' : 'fr-CA', {
        dateStyle: 'medium',
        timeStyle: 'short'
      });
    }
  },
  methods: {
    formatRate(value) {
      const number = Number(value);
      if (!Number.isFinite(number)) return '-';
      return new Intl.NumberFormat('fr-CA', {
        minimumFractionDigits: number >= 100 ? 2 : 6,
        maximumFractionDigits: number >= 100 ? 2 : 6
      }).format(number);
    },
    displayCurrencyName(rate) {
      if (!rate) return '';
      return this.isEnglish ? (rate.country_name_en || rate.currency_name) : (rate.country_name_fr || rate.currency_name);
    },
    async fetchRates() {
      this.loading = true;
      this.errorMessage = '';
      try {
        this.rates = await fetchPublicRates();
        this.updatedAt = this.rates.find((rate) => rate.updated_at)?.updated_at || null;
      } catch (error) {
        this.errorMessage = this.isEnglish ? 'Unable to load rates at the moment. Please try again shortly.' : 'Impossible de charger les taux pour le moment. Veuillez réessayer dans un instant.';
        console.error(error);
      } finally {
        this.loading = false;
      }
    }
  },
  mounted() {
    this.fetchRates();
  }
};
</script>

<style scoped>
@font-face {
  font-family: 'Digital-7';
  src: url('@/assets/fonts/digital-7.ttf') format('truetype');
  font-weight: normal;
  font-style: normal;
}

.rates-page {
  background: #f9f9f9;
  color: #001041;
}

.rates-hero {
  background:
    linear-gradient(to right, rgba(69, 166, 246, 0.72), rgba(0, 122, 204, 0.66)),
    url('@/assets/taux.jpg');
  background-position: top center;
  background-size: cover;
  color: #fff;
  padding: 80px 20px;
  text-align: center;
}

.hero-content {
  margin: 0 auto;
  max-width: 1200px;
  padding: 60px 20px;
}

.hero-content h1 {
  font-size: clamp(2.1rem, 5vw, 3rem);
  line-height: 1.1;
  margin: 8px 0 10px;
}

.hero-content p {
  font-size: 1.08rem;
  line-height: 1.65;
  margin: 0 auto;
  max-width: 650px;
}

.rates-content {
  margin: 0 auto;
  max-width: 960px;
  padding: 44px 20px 74px;
}

.rates-intro,
.currency-module {
  background: #fff;
  border: 1px solid #dce2ec;
  border-radius: 8px;
  box-shadow: 0 12px 30px rgba(0, 16, 65, 0.08);
}

.rates-intro {
  align-items: center;
  display: flex;
  gap: 20px;
  justify-content: space-between;
  margin-bottom: 18px;
  padding: 22px 24px;
}

.rates-intro h2,
.rates-card-header h3 {
  color: #001041;
  margin: 0;
}

.rates-intro h2 {
  font-size: clamp(1.35rem, 3vw, 2rem);
}

.eyebrow {
  color: #c39446;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0;
  margin: 0 0 7px;
  text-transform: uppercase;
}

.update-pill {
  background: #f8f3ea;
  border: 1px solid rgba(195, 148, 70, 0.32);
  border-radius: 8px;
  color: #4b5870;
  min-width: 220px;
  padding: 12px 14px;
}

.update-pill span,
.update-pill strong {
  display: block;
}

.update-pill span {
  font-size: 0.78rem;
  margin-bottom: 3px;
  text-transform: uppercase;
}

.update-pill strong {
  color: #001041;
  font-size: 0.95rem;
}

.status-card {
  align-items: center;
  background: #f8f3ea;
  border: 1px solid rgba(195, 148, 70, 0.32);
  border-radius: 8px;
  color: #001041;
  display: flex;
  gap: 10px;
  margin-bottom: 18px;
  padding: 14px 16px;
}

.status-card.error {
  background: #fff3f2;
  border-color: #f0c3bd;
  color: #8b1e1e;
}

.status-dot {
  animation: pulse 1s ease-in-out infinite;
  background: #c39446;
  border-radius: 50%;
  display: inline-block;
  height: 10px;
  width: 10px;
}

.currency-module {
  margin: 0 auto 18px;
  max-width: 980px;
  padding: 24px;
}

.rates-card-header {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: space-between;
}

.section-lead {
  color: #4b5870;
  margin: 8px 0 0;
  max-width: 560px;
}

.table-wrap {
  overflow-x: auto;
}

.currency-table {
  background: #10131a;
  border-collapse: separate;
  border-spacing: 0;
  border-radius: 8px;
  margin: 24px auto 12px;
  max-width: 900px;
  overflow: hidden;
  width: 100%;
}

.currency-table th {
  background: #001041;
  color: #fff;
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0;
  padding: 14px 18px 6px;
  text-align: right;
  text-transform: uppercase;
  white-space: nowrap;
}

.currency-table-subhead th {
  color: #c39446;
  font-size: 0.72rem;
  padding-bottom: 14px;
  padding-top: 0;
}

.currency-table th:first-child {
  text-align: left;
  width: 44%;
}

.currency-table td {
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  padding: 16px 18px;
  text-align: right;
  vertical-align: middle;
}

.currency-table tbody tr:nth-child(even) {
  background: rgba(255, 255, 255, 0.03);
}

.currency-cell {
  align-items: center;
  display: flex;
  gap: 0;
  min-width: 0;
  text-align: left;
}

.currency-table-text {
  color: #fff !important;
  display: inline-block;
  margin: 0 0 0 12px;
  vertical-align: middle;
}

.flag-icon {
  flex: 0 0 auto;
  float: none;
  height: 36px !important;
  vertical-align: middle;
  width: 54px !important;
}

.digital-text {
  color: #e53935;
  font-family: 'Digital-7', sans-serif;
  font-size: clamp(1.45rem, 2.4vw, 2rem);
  font-weight: 400;
  font-variant-numeric: tabular-nums;
  line-height: 1.25;
  white-space: nowrap;
}

.rate-notes {
  background: #fff;
  border-top: 1px solid #e5ebf3;
  color: #555;
  font-size: 0.9rem;
  padding: 12px 18px;
}

.rate-notes p {
  margin: 4px 0;
}

@keyframes pulse {
  0%, 100% {
    opacity: 0.4;
  }
  50% {
    opacity: 1;
  }
}

@media (max-width: 768px) {
  .rates-hero {
    padding: 56px 18px;
  }

  .hero-content {
    padding: 42px 0;
  }

  .rates-content {
    padding: 24px 12px 54px;
  }

  .rates-intro,
  .rates-card-header {
    display: block;
    padding: 14px;
  }

  .update-pill {
    margin-top: 16px;
    min-width: 0;
  }

  .currency-table th,
  .currency-table td {
    padding-left: 8px;
    padding-right: 8px;
  }

  .currency-table th {
    font-size: 0.76rem;
  }

  .flag-icon {
    height: 35px !important;
    margin-right: 10px;
    width: 42px !important;
  }

  .digital-text {
    font-size: 1.45rem;
  }
}
</style>
